import { create } from "zustand";
import {
  zonesFromFc,
  type CorePropKey,
  type CoreValues,
  type GeniusZone,
  type LabRow,
} from "../lib/geniusCores";

export interface CorePlacement {
  lng: number;
  lat: number;
  values: CoreValues;
}

interface GeniusCoreState {
  fieldId: string | null;
  fieldName: string;
  eventId: string | null;
  zones: GeniusZone[];
  step: number;
  placements: CorePlacement[][];
  /* * Per nutrient, one whole-zone high-res mean per zone, aligned with `zones`. */
  zoneMeans: Partial<Record<CorePropKey, (number | null)[]>> | null;
  labs: LabRow[];
  /* * Nutrient the table and the interpolated surface share. */
  nutrient: CorePropKey;
  /* * Draw the high-res krige of the corrected cores for `nutrient`. Not saved. */
  interpolateOn: boolean;
  interpolateStatus: "idle" | "running" | "ready" | "short" | "failed";
  miss: string | null;
  modelStatus: "loading" | "ready" | "missing";
  /* * Field-grid id (`/grid`.boundary_version_id) the cores were read against. */
  gridId: string | null;
  setGridId: (gridId: string | null) => void;
  /* * Stack content_version the zone means were read against. Save sends it so the Genius job refuses a moved model__uncorrected_* surface. Not an Rx stamp. */
  stackVersion: string | null;
  setStackVersion: (version: string | null) => void;
  start: (
    fieldId: string,
    fieldName: string,
    fc: GeoJSON.FeatureCollection,
    eventId: string | null,
  ) => void;
  place: (
    zoneIndex: number,
    lng: number,
    lat: number,
    values: CoreValues,
  ) => void;
  addCores: (
    cores: {
      zoneIndex: number;
      lng: number;
      lat: number;
      values: CoreValues;
    }[],
  ) => void;
  /* * Reads the uncorrected model at a coordinate once the stack grid is loaded. */
  read: ((lng: number, lat: number) => CoreValues) | null;
  setRead: (read: ((lng: number, lat: number) => CoreValues) | null) => void;
  setStep: (index: number) => void;
  setMiss: (message: string | null) => void;
  setModelStatus: (status: "loading" | "ready" | "missing") => void;
  setZoneMeans: (
    means: Partial<Record<CorePropKey, (number | null)[]>>,
  ) => void;
  setLabs: (rows: LabRow[]) => void;
  setNutrient: (nutrient: CorePropKey) => void;
  setInterpolateOn: (on: boolean) => void;
  setInterpolateStatus: (status: GeniusCoreState["interpolateStatus"]) => void;
  rescore: (read: (lng: number, lat: number) => CoreValues) => void;
  end: () => void;
}

const empty = {
  fieldId: null as string | null,
  fieldName: "",
  eventId: null as string | null,
  zones: [] as GeniusZone[],
  step: 0,
  placements: [] as CorePlacement[][],
  zoneMeans: null as Partial<Record<CorePropKey, (number | null)[]>> | null,
  labs: [] as LabRow[],
  nutrient: "ph" as CorePropKey,
  interpolateOn: false,
  interpolateStatus: "idle" as const,
  miss: null as string | null,
  modelStatus: "loading" as const,
  gridId: null as string | null,
  stackVersion: null as string | null,
  read: null as ((lng: number, lat: number) => CoreValues) | null,
};

export const useGeniusCoreStore = create<GeniusCoreState>((set) => ({
  ...empty,
  start: (fieldId, fieldName, fc, eventId) => {
    const zones = zonesFromFc(fc);
    set({
      fieldId,
      fieldName,
      eventId,
      zones,
      step: 0,
      placements: zones.map(() => []),
      zoneMeans: null,
      labs: [],
      nutrient: "ph",
      interpolateOn: false,
      interpolateStatus: "idle",
      miss: null,
      modelStatus: "loading",
      gridId: null,
      stackVersion: null,
    });
  },
  place: (zoneIndex, lng, lat, values) =>
    set((s) => {
      const placements = s.placements.slice();
      const here = placements[zoneIndex] ? [...placements[zoneIndex]] : [];
      here.push({ lng, lat, values });
      placements[zoneIndex] = here;
      return { placements, miss: null };
    }),
  addCores: (cores) =>
    set((s) => {
      const placements = s.placements.map((group) => group.slice());
      for (const core of cores) {
        const here = placements[core.zoneIndex];
        if (!here) continue;
        here.push({ lng: core.lng, lat: core.lat, values: core.values });
      }
      return { placements, miss: null };
    }),
  setRead: (read) => set({ read }),
  setStep: (index) =>
    set((s) => ({
      step: Math.max(0, Math.min(s.zones.length - 1, index)),
      miss: null,
    })),
  setMiss: (miss) => set({ miss }),
  setModelStatus: (modelStatus) => set({ modelStatus }),
  setGridId: (gridId) => set({ gridId }),
  setStackVersion: (stackVersion) => set({ stackVersion }),
  setZoneMeans: (zoneMeans) => set({ zoneMeans }),
  setLabs: (labs) => set({ labs }),
  setNutrient: (nutrient) => set({ nutrient }),
  setInterpolateOn: (interpolateOn) =>
    set({
      interpolateOn,
      interpolateStatus: interpolateOn ? "running" : "idle",
    }),
  setInterpolateStatus: (interpolateStatus) => set({ interpolateStatus }),
  rescore: (read) =>
    set((s) => ({
      placements: s.placements.map((group) =>
        group.map((p) => ({ ...p, values: read(p.lng, p.lat) })),
      ),
    })),
  end: () => set({ ...empty }),
}));
